import React, {
    useState,
    useEffect,
    useRef,
    useImperativeHandle,
    forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Checkbox from '@mui/material/Checkbox';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import InputAdornment from '@mui/material/InputAdornment';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import Filter from './filter';
import Validator, { useValidate, checkValidity } from '../validator';

import defaultConfig from '../../data/eexportd';
import SystemService from '../../data/system-service';
import { Dataset } from '@mui/icons-material';

const useStyles = makeStyles(theme => ({
    fullWidth: {
        width: '100%',
    },
    timezone: {
        width: '120px',
    },
    hideFileInput: {
        display: 'none',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    certBox: {
        marginTop: theme.spacing(2),
        padding: '5px 8px',
        border: `1px solid ${theme.palette.primary.main}`,
        background: theme.styled.paper.background,
        color: theme.palette.text.primary,
    }
}));

const Kafka = forwardRef((props, ref) => {
    const classes = useStyles();
    const [, setSystemConstants] = useState({
        facilities: [],
        severities: [],
        timezones: [],
    });
    const filterRef = useRef(null);
    const [init, setInit] = useState(true);
    const [original, setOriginal] = useState(_cloneDeep({
        ...defaultConfig,
        ...props.data,
    }));
    const [data, setData] = useState(_cloneDeep({
        ...defaultConfig,
        ...props.data
    }));
    const [, setChanges] = useState(false);
    const dataForm = useValidate({});

    useImperativeHandle(ref, () => ({
        getData: () => {
            const f = filterRef.current.getFilter();
            return {
                ...data,
                filterconf: f,
            };
        },
    }));

    useEffect(() => {
        SystemService.getConstants().then((res) => {
            setSystemConstants(res);
        });
    }, []);

    useEffect(() => {
        const t = !_isEqual(data, original);
        setChanges(t);
        if (t) {
            props.onChange(props.name, t, data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (props.data && props.data.dest) {
            const d = { ...props.data };
            if (d.prune_type === '') {
                d.prune_type = 'inactive';
            }
            if (props.data.dest.proto === 'UDP') {
                d.dest.proto = 'TCP';
            }

            if (init) {
                setOriginal(_cloneDeep({ ...defaultConfig, ...d }));
                setData(_cloneDeep({ ...defaultConfig, ...d }));
                setInit(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props]);

    const handleBrokers = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        d.kafka.brokers = e.target.value;
        setData(d);
    };

    const handleTopic = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        d.kafka.topic = e.target.value;
        setData(d);
    };

    const handleRetries = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        d.kafka.retries = isNaN(parseInt(e.target.value)) ? '' : parseInt(e.target.value);
        setData(d);
    };

    const handlePruneTypeChange = (e) => {
        const d = { ...data };
        d.prune_type = e.target.value;
        setData(d);
    };

    const handlePruneKeysChange = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        d.prune_keys = e.target.value ? e.target.value.replace(/\s/g, '').split(',') : [];
        setData(d);
    };

    const handleCompression = (e) => {
        const d = { ...data };
        d.kafka.compression = e.currentTarget.checked;
        setData(d);
    };

    const handleProtocol = (e, component) => {
        const d = { ...data };
        d.dest.proto = component.props.value;
        setData(d);
    };

    const handleCACert = (e) => {
        const d = { ...data };
        d.tls_cert.enable = e.currentTarget.checked;
        setData(d);
    };

    const fileHandler = (file) => {
        const reader = new FileReader();
        return new Promise((resolve, reject) => {
            reader.onload = event => resolve(event.target.result);
            reader.onerror = error => reject(error);
            reader.readAsText(file);
        });
    };

    const handleTLSContents = (e) => {
        const f = e.target.files[0];
        fileHandler(e.target.files[0]).then(content => {
            const d = { ...data };
            d.tls_cert.contents = content;
            d.tls_cert.file_name = f.name;
            setData(d);
        }).catch(err => console.error(err));
    };

    const handleTLSClientKey = (e) => {
        const f = e.target.files[0];
        fileHandler(e.target.files[0]).then(content => {
            const d = { ...data };
            d.tls_client_key.contents = content;
            d.tls_client_key.file_name = f.name;
            setData(d);
        }).catch(err => console.error(err));
    };

    const handleTLSClientCert = (e) => {
        const f = e.target.files[0];
        fileHandler(e.target.files[0]).then(content => {
            const d = { ...data };
            d.tls_client_cert.contents = content;
            d.tls_client_cert.file_name = f.name;
            setData(d);
        }).catch(err => console.error(err));
    };

    const handleFIPS = (e) => {
        const d = { ...data };
        d.tls_fips = e.currentTarget.checked;
        setData(d);
    };

    const handleClientAuth = (e) => {
        const d = { ...data };
        d.tls_client_auth = e.currentTarget.checked;
        d.tls_client_cert.enable = e.currentTarget.checked;
        d.tls_client_key.enable = e.currentTarget.checked;
        setData(d);
    };

    const handleVerify = (k, i, close) => {
        props.onVerify(k, i, close);
    };

    const handleCancel = (k, i) => {
        props.onCancel(k, i);
    };

    const handleEventTypeAlerts = (e) => {
        const d = { ...data };
        if (e.currentTarget.checked) {
            d.event_types.push('alert');
        } else {
            d.event_types.splice(d.event_types.indexOf('alert'), 1);
        }
        setData(d);
    };

    const handleEventTypeAnomalies = (e) => {
        const d = { ...data };
        if (e.currentTarget.checked) {
            d.event_types.push('anomaly');
        } else {
            d.event_types.splice(d.event_types.indexOf('anomaly'), 1);
        }
        setData(d);
    };

    const handleEventTypeMetadata = (e) => {
        const d = { ...data };
        if (e.currentTarget.checked) {
            d.event_types.push('bro_log');
        } else {
            d.event_types.splice(d.event_types.indexOf('bro_log'), 1);
        }
        setData(d);
    };

    const handleEventTypeDPIMetadata = (e) => {
        const d = { ...data };
        if (e.currentTarget.checked) {
            d.event_types.push('dpi');
        } else {
            d.event_types.splice(d.event_types.indexOf('dpi'), 1);
        }
        setData(d);
    };

    const handleEventTypeHealthAlerts = (e) => {
        const d = { ...data };
        if (e.currentTarget.checked) {
            d.event_types.push('health_alert');
        } else {
            d.event_types.splice(d.event_types.indexOf('health_alert'), 1);
        }
        setData(d);
    };

    const handleUpdateFilter = (filter) => {
        setData({
            ...data,
            filterconf: filter,
        });
    };

    const handlePruningKeyReference = (e) => {
        window.open('/support/filter_reference');
    };

    const handleMessageMaxBytes = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        d.kafka.message_max_bytes = isNaN(parseInt(e.target.value)) ? '' : parseInt(e.target.value);
        setData(d);
    };

    const handleQueueBufferingMaxMessages = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        d.kafka.queue_buffering_max_messages = isNaN(parseInt(e.target.value)) ? '' : parseInt(e.target.value);
        setData(d);
    };  

    const handleQueueBufferingMaxKbytes = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        d.kafka.queue_buffering_max_kbytes = isNaN(parseInt(e.target.value)) ? '' : parseInt(e.target.value);
        setData(d);
    };

    const handleQueueBufferingMaxMs = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        d.kafka.queue_buffering_max_ms = isNaN(parseInt(e.target.value)) ? '' : parseInt(e.target.value);
        setData(d);
    };  

    const handleSslCipherSuites = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        d.kafka.ssl_cipher_suites = e.target.value;
        setData(d);
    };

    const handleAddHeader = () => {
        const d = { ...data };
        if (!d.kafka.headers) d.kafka.headers = [];
        d.kafka.headers = [...d.kafka.headers, { key: '', value: '' }];
        setData(d);
    };

    const handleRemoveHeader = (index) => {
        const d = { ...data };
        d.kafka.headers = d.kafka.headers.filter((_, i) => i !== index);
        setData(d);
    };

    const handleHeaderKeyChange = (index, e) => {
        const d = { ...data };
        d.kafka.headers = [...d.kafka.headers];
        d.kafka.headers[index] = { ...d.kafka.headers[index], key: e.target.value };
        setData(d);
    };

    const handleHeaderValueChange = (index, e) => {
        const d = { ...data };
        d.kafka.headers = [...d.kafka.headers];
        d.kafka.headers[index] = { ...d.kafka.headers[index], value: e.target.value };
        setData(d);
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box>
                    <Box display="inline-block" pr={5}>Event Type(s):</Box>
                    <FormControlLabel
                        label="Alerts"
                        onChange={(e) => { handleEventTypeAlerts(e) }}
                        control={<Checkbox disabled={(!props.perms.write || props.policySource === 'mc2')} checked={data.event_types.indexOf('alert') !== -1} />}
                    />
                    {props.cmc && <FormControlLabel
                        label="Anomalies"
                        onChange={(e) => { handleEventTypeAnomalies(e) }}
                        control={<Checkbox disabled={(!props.perms.write || props.policySource === 'mc2')} checked={data.event_types.indexOf('anomaly') !== -1} />}
                    />}
                    <FormControlLabel
                        label="Metadata"
                        onChange={(e) => { handleEventTypeMetadata(e) }}
                        control={<Checkbox disabled={(!props.perms.write || props.policySource === 'mc2')} checked={data.event_types.indexOf('bro_log') !== -1} />}
                    />
                    <FormControlLabel
                        label="DPI Metadata"
                        onChange={(e) => { handleEventTypeDPIMetadata(e) }}
                        control={<Checkbox disabled={(!props.perms.write || props.policySource === 'mc2')} checked={data.event_types.indexOf('dpi') !== -1} />}
                    />
                    <FormControlLabel
                        label="Health Alerts"
                        onChange={(e) => { handleEventTypeHealthAlerts(e) }}
                        control={<Checkbox disabled={(!props.perms.write || props.policySource === 'mc2')} checked={data.event_types.indexOf('health_alert') !== -1} />}
                    />
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Box>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel id={'raw-protocol-label-' + props.index}>Protocol</FormLabel>
                        <Select disabled={(!props.perms.write || props.policySource === 'mc2')} id={'raw-protocol-label-' + props.index} value={data.dest.proto} onChange={handleProtocol}>
                            <MenuItem value="TCP">TCP</MenuItem>
                            <MenuItem value="TLS">TLS</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel>Brokers</FormLabel>
                        <Validator
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            valueType="ipList"
                            errorText="Must be a comma seperated list of broker hostnames/IPs"
                            fullWidth
                            value={data.kafka && data.kafka.brokers}
                            onChange={handleBrokers}
                        />
                    </FormControl>
                </Box>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel>Topic</FormLabel>
                        <Validator
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            valueType="kafkaTopicName"
                            errorText="Must be a valid topic name"
                            fullWidth
                            value={data.kafka && data.kafka.topic}
                            onChange={handleTopic}
                        />
                    </FormControl>
                </Box>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel>Retries</FormLabel>
                        <Validator
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            valueType="range"
                            errorText="Retries must be within 0 and 500"
                            fullWidth
                            inputProps={{
                                min: 0,
                                max: 500,
                            }}
                            value={data.kafka && data.kafka.retries}
                            onChange={handleRetries}
                        />
                    </FormControl>
                </Box>
                <Box mt={3} display="flex" justifyContent="space-between">
                    <Box>
                        <Typography>Event Pruning Type:</Typography>
                        <RadioGroup row value={data.prune_type} onChange={handlePruneTypeChange}>
                            <FormControlLabel
                                disabled={(!props.perms.write || props.policySource === 'mc2')}
                                label="Inactive"
                                aria-label="inactive"
                                control={<Radio value='inactive' />} />
                            <FormControlLabel
                                disabled={(!props.perms.write || props.policySource === 'mc2')}
                                label="Remove"
                                aria-label="remove"
                                control={<Radio value='black' />} />
                            <FormControlLabel
                                disabled={(!props.perms.write || props.policySource === 'mc2')}
                                label="Add"
                                aria-label="add"
                                control={<Radio value='white' />} />
                        </RadioGroup>
                    </Box>
                    <Box>
                        <Button variant="contained" color="primary" onClick={handlePruningKeyReference}>Pruning key reference</Button>
                    </Box>
                </Box>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel>Event Pruning Keys:</FormLabel>
                        <Validator
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            allowEmpty
                            valueType="keyPairList"
                            errorText="Must be valid comma separated key pairs ex. bricata:dest_location"
                            fullWidth
                            value={data.prune_keys ? data.prune_keys.join(',') : []}
                            onChange={handlePruneKeysChange}
                        />
                    </FormControl>
                </Box>
                <Box mt={3}>
                    <FormControlLabel
                        labelPlacement="start"
                        label="Compression"
                        disabled={(!props.perms.write || props.policySource === 'mc2')}
                        control={
                            <Checkbox
                                checked={data.kafka && data.kafka.compression}
                                name=""
                                onChange={handleCompression}
                            />
                        }
                    />
                </Box>
            </Grid>
            <Grid item container xs={6} alignContent="flex-start" spacing={2}>
                <Grid item xs={12}>
                    <Box>
                        <FormControl className={classes.fullWidth}>
                            <FormLabel>Message Max Bytes</FormLabel>
                            <Validator
                                disabled={(!props.perms.write || props.policySource === 'mc2')}
                                valueType="range"
                                errorText="Message max bytes must be within 1000 and 1000000000"
                                fullWidth
                                inputProps={{
                                    min: 1000,
                                    max: 1000000000,
                                }}
                                value={data.kafka && data.kafka.message_max_bytes}
                                onChange={handleMessageMaxBytes}
                            />
                        </FormControl>
                    </Box>
                    <Box mt={3}>
                        <FormControl className={classes.fullWidth}>
                            <FormLabel>Buffering Max Messages</FormLabel>
                            <Validator
                                disabled={(!props.perms.write || props.policySource === 'mc2')}
                                valueType="range"
                                errorText="Buffering max messages must be within 0 and 2147483647"
                                fullWidth
                                inputProps={{
                                    min: 0,
                                    max: 2147483647,
                                }}
                                value={data.kafka && data.kafka.queue_buffering_max_messages}
                                onChange={handleQueueBufferingMaxMessages}
                            />
                        </FormControl>
                    </Box>
                    <Box mt={3}>
                        <FormControl className={classes.fullWidth}>
                            <FormLabel>Buffering Max Kbytes</FormLabel>
                            <Validator
                                disabled={(!props.perms.write || props.policySource === 'mc2')}
                                valueType="range"
                                errorText="Buffering max kbytes must be within 0 and 2147483647"
                                fullWidth
                                inputProps={{
                                    min: 1,
                                    max: 2147483647,
                                }}
                                value={data.kafka && data.kafka.queue_buffering_max_kbytes}
                                onChange={handleQueueBufferingMaxKbytes}
                            />
                        </FormControl>
                    </Box>
                    <Box mt={3}>
                        <FormControl className={classes.fullWidth}>
                            <FormLabel>Buffering Max ms</FormLabel>
                            <Validator
                                disabled={(!props.perms.write || props.policySource === 'mc2')}
                                valueType="range"
                                errorText="Buffering max ms must be within 0 and 900000"
                                fullWidth
                                inputProps={{
                                    min: 0,
                                    max: 900000,
                                }}
                                value={data.kafka && data.kafka.queue_buffering_max_ms}
                                onChange={handleQueueBufferingMaxMs}
                            />
                        </FormControl>
                    </Box>
                    <Box mt={3}>
                        <FormControl className={classes.fullWidth}>
                            <FormLabel>SSL Cipher Suites</FormLabel>
                            <Validator
                                disabled={(!props.perms.write || props.policySource === 'mc2')}
                                valueType="kafkaSslCipherSuites"
                                errorText="Must be a valid colon-separated cipher suite list (e.g., HIGH:!kRSA:!MD5:!RC4:!SHA1)"
                                allowEmpty
                                fullWidth
                                value={data.kafka && data.kafka.ssl_cipher_suites}
                                onChange={handleSslCipherSuites}
                                placeholder="HIGH:!kRSA:!MD5:!RC4:!SHA1"
                            />
                        </FormControl>
                    </Box>
                    <Box mt={3}>
                        <FormControlLabel
                            labelPlacement="start"
                            label="FIPS"
                            disabled={(!props.perms.write || props.policySource === 'mc2') || data.dest.proto !== 'TLS'}
                            control={
                                <Checkbox
                                    checked={data.tls_fips}
                                    name=""
                                    onChange={handleFIPS}
                                />
                            }
                        />
                    </Box>
                </Grid>
                <Grid item xs={6}>
                    <Box mt={3}>
                        <FormControlLabel
                            labelPlacement="start"
                            label="CA Cert"
                            disabled={(!props.perms.write || props.policySource === 'mc2') || data.dest.proto !== 'TLS'}
                            control={
                                <Checkbox
                                    checked={data.tls_cert.enable}
                                    name=""
                                    onChange={handleCACert}
                                />
                            }
                        />
                    </Box>
                    <Box mt={3}>
                        <input
                            id={'kafka-cert-file-' + props.index}
                            className={classes.hideFileInput}
                            type="file"
                            onChange={handleTLSContents}
                            disabled={(!props.perms.write || props.policySource === 'mc2') || data.dest.proto !== 'TLS' || !data.tls_cert.enable}
                        />
                        <label htmlFor={'kafka-cert-file-' + props.index}>
                            <Button
                                variant="contained"
                                color="primary"
                                aria-label="upload cert"
                                component="span"
                                disabled={(!props.perms.write || props.policySource === 'mc2') || data.dest.proto !== 'TLS' || !data.tls_cert.enable}
                            >
                                Upload Cert
                            </Button>
                        </label>
                    </Box>
                    <Box className={classes.certBox} display={data.tls_cert.file_name ? 'flex' : 'none'}>
                        {data.tls_cert.file_name ? 'Filename: ' : ''} {data.tls_cert.file_name}
                    </Box>
                </Grid>
                <Grid item xs={6}>
                    <Box mt={3}>
                        <FormControlLabel
                            labelPlacement="start"
                            label="TLS Client Auth"
                            disabled={(!props.perms.write || props.policySource === 'mc2') || data.dest.proto !== 'TLS'}
                            control={
                                <Checkbox
                                    checked={data.tls_client_auth}
                                    name="tls_client_auth"
                                    onChange={handleClientAuth}
                                />
                            }
                        />
                    </Box>
                    <Box mt={3}>
                        <input
                            id={'kafka-client-key-' + props.index}
                            className={classes.hideFileInput}
                            type="file"
                            onChange={handleTLSClientKey}
                            disabled={(!props.perms.write || props.policySource === 'mc2') || !data.tls_client_auth || data.dest.proto !== 'TLS'}
                        />
                        <label htmlFor={'kafka-client-key-' + props.index}>
                            <Button
                                variant="contained"
                                color="primary"
                                aria-label="upload key"
                                component="span"
                                disabled={(!props.perms.write || props.policySource === 'mc2') || data.dest.proto !== 'TLS' || !data.tls_client_auth}
                            >
                                Upload Key
                            </Button>
                        </label>
                    </Box>
                    <Box className={classes.certBox} display={data.tls_client_key && data.tls_client_key.file_name ? 'flex' : 'none'}>
                        {data.tls_client_key && data.tls_client_key.file_name ? 'Filename: ' : ''} {data.tls_client_key && data.tls_client_key.file_name}
                    </Box>
                    <Box mt={3}>
                        <input
                            id={'kafka-client-cert-' + props.index}
                            className={classes.hideFileInput}
                            type="file"
                            onChange={handleTLSClientCert}
                            disabled={(!props.perms.write || props.policySource === 'mc2') || data.dest.proto !== 'TLS' || !data.tls_client_auth}
                        />
                        <label htmlFor={'kafka-client-cert-' + props.index}>
                            <Button
                                variant="contained"
                                color="primary"
                                aria-label="upload cert"
                                component="span"
                                disabled={(!props.perms.write || props.policySource === 'mc2') || data.dest.proto !== 'TLS' || !data.tls_client_auth}
                            >
                                Upload Cert
                            </Button>
                        </label>
                    </Box>
                    <Box className={classes.certBox} display={data.tls_client_cert && data.tls_client_cert.file_name ? 'flex' : 'none'}>
                        {data.tls_client_cert && data.tls_client_cert.file_name ? 'Filename: ' : ''} {data.tls_client_cert && data.tls_client_cert.file_name}
                    </Box>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Box mt={2}>
                    <Box mb={2} display="flex" justifyContent="space-between">
                        <Typography>Kafka Headers</Typography>
                        {(props.perms.write && props.policySource !== 'mc2') && <Box>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAddHeader}
                            >
                                <AddIcon /> Header
                            </Button>
                        </Box>}
                    </Box>
                    {(data.kafka && data.kafka.headers || []).map((header, idx) => (
                        <Box key={'kafka-header-' + idx} mb={2} display="flex" alignItems="center">
                            <TextField
                                disabled={(!props.perms.write || props.policySource === 'mc2')}
                                label="Key"
                                fullWidth
                                value={header.key || ''}
                                onChange={(e) => handleHeaderKeyChange(idx, e)}
                                placeholder="e.g. X-Auth"
                                style={{ marginRight: 8 }}
                            />
                            <TextField
                                disabled={(!props.perms.write || props.policySource === 'mc2')}
                                label="Value"
                                fullWidth
                                value={header.value || ''}
                                onChange={(e) => handleHeaderValueChange(idx, e)}
                                placeholder="Header value"
                                inputProps={{ maxLength: 500 }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title="Delete header">
                                                <IconButton
                                                    disabled={(!props.perms.write || props.policySource === 'mc2')}
                                                    color="secondary"
                                                    aria-label="delete header"
                                                    onClick={() => handleRemoveHeader(idx)}
                                                    size="large">
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Box>
                    ))}
                </Box>
            </Grid>
            <Filter
                filter={data.filterconf}
                ref={filterRef}
                onUpdate={handleUpdateFilter}
                onVerify={() => { handleVerify(props.name, props.index, false) }}
                policySource={props.policySource}
                perms={props.perms}
            />
            {(props.perms.write || props.policySource !== 'mc2') && <Grid item xs={12}>
                <Box mt={2}>
                    <Button
                        className={classes.buttonRight}
                        variant="contained"
                        color="primary"
                        aria-label="save"
                        onClick={() => { handleVerify(props.name, props.index, true) }}
                        disabled={!(checkValidity(dataForm.get()))}
                    >Verify</Button>
                    <Button className={classes.buttonRight} variant="contained" color="secondary" aria-label="cancel" onClick={() => { handleCancel(props.name, props.index) }}>Cancel</Button>
                </Box>
            </Grid>}
        </Grid>
    );
});

Kafka.propTypes = {
    name: PropTypes.string,
    index: PropTypes.number,
    data: PropTypes.object,
    policySource: PropTypes.string,
    onChange: PropTypes.func,
    onVerify: PropTypes.func,
    onCancel: PropTypes.func,
    perms: PropTypes.object,
    cmc: PropTypes.bool,
};

export default Kafka;
